import React from 'react';
import { Box, Typography, Select, MenuItem, Switch } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import TimerIcon from '@mui/icons-material/Timer';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';

type EscalationBehavior = 'ems' | 'org';

interface FeatureConfigCardProps {
    checkOnMeEnabled: boolean | undefined;
    escalation: EscalationBehavior;
    isOverride: boolean;
    onToggleCheckOnMe: (enabled: boolean) => void;
    onEscalationChange: (value: EscalationBehavior) => void;
    onResetToDefault?: () => void;
}

const ESCALATION_HINTS: Record<EscalationBehavior, string> = {
    ems: 'When a timer expires, Dispatch will immediately contact EMS for help.',
    org: 'Dispatch will contact your designated emergency contacts before dispatching external help.',
};

export const FeatureConfigCard: React.FC<FeatureConfigCardProps> = ({
    checkOnMeEnabled,
    escalation,
    isOverride,
    onToggleCheckOnMe,
    onEscalationChange,
    onResetToDefault,
}) => {
    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                padding: '20px',
                gridColumn: '1 / -1',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '14px' }}>
                <TuneIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Feature Configuration</Typography>
            </Box>

            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mb: '16px' }}>
                Configure Check On Me for POM Users on this team. When set to "Org Default," the team inherits the
                organization-level setting. Override to enable or disable specifically for this team.
            </Typography>

            {/* Check On Me Section */}
            <Box
                sx={{
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        backgroundColor: GT_COLORS.headerBg,
                        borderBottom: `1px solid ${GT_COLORS.border}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimerIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Check On Me</Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '.3px',
                            backgroundColor: isOverride ? GT_COLORS.overrideCustomBg : GT_COLORS.overrideOrgBg,
                            color: isOverride ? GT_COLORS.overrideCustomColor : GT_COLORS.overrideOrgColor,
                        }}
                    >
                        {isOverride ? 'Team Override' : 'Org Default'}
                    </Box>
                </Box>

                {/* Body */}
                <Box sx={{ padding: '14px 16px' }}>
                    {/* Toggle */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: '14px' }}>Enable Check On Me</Typography>
                            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint, mt: '1px' }}>
                                Allow POM Users on this team to start Check On Me timers.
                            </Typography>
                        </Box>
                        <Switch
                            checked={checkOnMeEnabled ?? false}
                            onChange={(e) => onToggleCheckOnMe(e.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: GT_COLORS.primary,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: GT_COLORS.primary,
                                },
                            }}
                        />
                    </Box>

                    {/* Escalation dropdown — only when enabled */}
                    {checkOnMeEnabled && (
                        <Box sx={{ mt: '12px' }}>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: GT_COLORS.textMuted,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.5px',
                                    mb: '4px',
                                }}
                            >
                                Escalation Behavior
                            </Typography>
                            <Select
                                fullWidth
                                size="small"
                                value={escalation}
                                onChange={(e) => onEscalationChange(e.target.value as EscalationBehavior)}
                                sx={{ fontSize: '14px', borderRadius: '8px', mb: '4px' }}
                            >
                                <MenuItem value="ems" sx={{ fontSize: '14px' }}>Escalate to Dispatch with EMS</MenuItem>
                                <MenuItem value="org" sx={{ fontSize: '14px' }}>Escalate to Dispatch with Org Contacts</MenuItem>
                            </Select>
                            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                                {ESCALATION_HINTS[escalation]}
                            </Typography>
                        </Box>
                    )}

                    {/* Reset to default */}
                    {isOverride && onResetToDefault && (
                        <Box sx={{ mt: '12px', pt: '12px', borderTop: `1px solid ${GT_COLORS.borderLight}` }}>
                            <Typography
                                component="a"
                                onClick={onResetToDefault}
                                sx={{
                                    fontSize: '13px',
                                    color: GT_COLORS.primary,
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Reset to Org Default
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
